import React, { useEffect, useMemo, useState } from "react";
import { api, fmtINR, fmtNum, todayISO } from "../lib/api";
import { X, Trash2, Plus, Minus, ShoppingCart, Receipt, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import BillShareModal from "../components/BillShareModal";

const CAT_LABEL = { chicken: "Chicken", mutton: "Mutton", offals: "Offals" };

export default function POS() {
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState({}); // product_id -> price
  const [activeCat, setActiveCat] = useState("chicken");
  const [cart, setCart] = useState([]); // {product_id, name, category, rate, kg, amount, mode}
  const [paid, setPaid] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [customers, setCustomers] = useState([]);
  const [creditCustId, setCreditCustId] = useState("");
  const [lastSale, setLastSale] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    const [pRes, prRes, cRes] = await Promise.all([
      api.get("/products?active_only=true"),
      api.get(`/daily-prices?date=${todayISO()}&channel=retail`),
      api.get("/customers"),
    ]);
    setProducts(pRes.data);
    const map = {};
    prRes.data.forEach((p) => (map[p.product_id] = p.price_per_kg));
    setPrices(map);
    setCustomers(cRes.data);
  };

  useEffect(() => {
    load();
  }, []);

  const cats = useMemo(() => {
    const out = {};
    products.forEach((p) => {
      if (!out[p.category]) out[p.category] = [];
      out[p.category].push(p);
    });
    return out;
  }, [products]);

  const addToCart = (product) => {
    const rate = prices[product.id] || 0;
    if (!rate) {
      toast.error(`Set today's rate for ${product.name} first`);
      return;
    }
    const unit = product.unit || "kg";
    const initQty = unit === "piece" ? 1 : 1;
    setCart((c) => [
      ...c,
      {
        line_id: Math.random().toString(36).slice(2),
        product_id: product.id,
        product_name: product.name,
        category: product.category,
        unit,
        rate,
        kg: initQty,
        amount: rate * initQty,
        mode: "kg",
      },
    ]);
  };

  const updateLine = (line_id, field, val) => {
    setCart((c) =>
      c.map((it) => {
        if (it.line_id !== line_id) return it;
        const num = parseFloat(val) || 0;
        if (field === "kg") {
          const q = it.unit === "piece" ? Math.round(num) : num;
          return { ...it, kg: q, amount: +(q * it.rate).toFixed(2), mode: "kg" };
        }
        if (field === "amount") {
          let kg = it.rate > 0 ? num / it.rate : 0;
          if (it.unit === "piece") kg = Math.round(kg);
          kg = +kg.toFixed(it.unit === "piece" ? 0 : 3);
          return { ...it, amount: num, kg, mode: "amount" };
        }
        if (field === "rate") {
          const amt = +(it.kg * num).toFixed(2);
          return { ...it, rate: num, amount: amt };
        }
        return it;
      })
    );
  };

  const removeLine = (line_id) => setCart((c) => c.filter((it) => it.line_id !== line_id));

  const totals = useMemo(() => {
    const totalKg = cart
      .filter((it) => (it.unit || "kg") !== "piece")
      .reduce((s, it) => s + (it.kg || 0), 0);
    const totalPcs = cart
      .filter((it) => it.unit === "piece")
      .reduce((s, it) => s + (it.kg || 0), 0);
    const totalAmount = Math.round(cart.reduce((s, it) => s + (it.amount || 0), 0));
    return { totalKg, totalPcs, totalAmount };
  }, [cart]);

  const quickAddCustomer = async () => {
    const nm = window.prompt("New customer name:");
    if (!nm || !nm.trim()) return;
    const ph = window.prompt("Phone (optional):") || "";
    const res = await api.post("/customers", { name: nm.trim(), phone: ph.trim() });
    setCustomers((c) => [...c, res.data]);
    setCreditCustId(res.data.id);
    toast.success(`Customer "${res.data.name}" added`);
  };

  const checkout = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    if (payMode === "credit" && !creditCustId) {
      toast.error("Select a customer for credit bill");
      return;
    }
    try {
      const creditCust = customers.find((c) => c.id === creditCustId);
      const payload = {
        date: todayISO(),
        channel: "pos",
        items: cart.map((it) => ({
          product_id: it.product_id,
          product_name: it.product_name,
          category: it.category,
          unit: it.unit || "kg",
          kg: it.kg,
          rate: it.rate,
          amount: it.amount,
        })),
        payment_mode: payMode,
        paid_amount:
          payMode === "credit"
            ? parseFloat(paid) || 0
            : parseFloat(paid) || totals.totalAmount,
        customer_id: payMode === "credit" ? creditCustId : null,
        customer_name: payMode === "credit" ? creditCust?.name || "" : "",
      };
      const res = await api.post("/sales", payload);
      toast.success(`Bill #${res.data.bill_no} saved · ${fmtINR(totals.totalAmount)}`);
      setLastSale(res.data);
      setShareOpen(true);
      setCart([]);
      setPaid("");
      setPayMode("cash");
      setCreditCustId("");
    } catch (e) {
      toast.error("Failed to save bill");
    }
  };

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-3.5rem)] lg:max-h-screen lg:overflow-hidden bg-zinc-50" data-testid="pos-page">
      {/* Left pane */}
      <div className="flex-1 lg:overflow-y-auto p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="border border-zinc-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-100"
              data-testid="pos-back-btn"
            >
              <ArrowLeft className="h-4 w-4 inline mr-1" />
              Back
            </Link>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Counter
              </div>
              <h1 className="font-display text-3xl font-bold tracking-tight">
                Retail POS
              </h1>
            </div>
          </div>
          <div className="text-xs text-zinc-500">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "short",
            })}
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex gap-0 border-b border-zinc-200 mb-5" data-testid="pos-tabs">
          {Object.keys(cats).map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              data-testid={`pos-tab-${c}`}
              className={`px-5 py-3 text-sm font-medium uppercase tracking-wider border-b-2 transition-colors ${
                activeCat === c
                  ? "border-sky-600 text-zinc-950"
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {CAT_LABEL[c] || c}
              <span className="ml-2 text-xs text-zinc-400">({cats[c]?.length || 0})</span>
            </button>
          ))}
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {(cats[activeCat] || []).map((p) => {
            const rate = prices[p.id] || 0;
            const hasRate = rate > 0;
            const unit = p.unit || "kg";
            return (
              <button
                key={p.id}
                onClick={() => addToCart(p)}
                disabled={!hasRate}
                data-testid={`pos-product-${p.id}`}
                className={`text-left border bg-white p-4 min-h-[120px] flex flex-col justify-between transition-all ${
                  hasRate
                    ? "border-zinc-200 hover:border-sky-600 hover:shadow-[2px_2px_0_0_#0284c7] active:scale-[0.98]"
                    : "border-zinc-200 opacity-50 cursor-not-allowed"
                }`}
              >
                <div className="text-sm font-semibold leading-tight">{p.name}</div>
                <div className="mt-3 flex items-end justify-between">
                  <div className="font-mono-num font-bold text-xl">
                    {hasRate ? `₹${fmtNum(rate, 0)}` : "—"}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    /{unit === "piece" ? "pc" : "kg"}
                  </div>
                </div>
              </button>
            );
          })}
          {(cats[activeCat] || []).length === 0 && (
            <div className="col-span-full text-center text-sm text-zinc-500 py-12 border border-dashed border-zinc-300">
              No products in this category
            </div>
          )}
        </div>
      </div>

      {/* Cart pane */}
      <aside
        className="w-full lg:w-[420px] xl:w-[460px] border-t lg:border-t-0 lg:border-l border-zinc-200 bg-white flex flex-col lg:h-full"
        data-testid="pos-cart"
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-sky-600" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Current Bill
              </div>
              <div className="font-display font-bold text-lg">
                {cart.length} {cart.length === 1 ? "item" : "items"}
              </div>
            </div>
          </div>
          {cart.length > 0 && (
            <button
              onClick={() => setCart([])}
              data-testid="pos-clear-btn"
              className="text-xs uppercase tracking-wider text-zinc-500 hover:text-sky-600"
            >
              Clear
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {cart.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">
              Tap a product to add it
            </div>
          ) : (
            cart.map((it) => (
              <div
                key={it.line_id}
                className="border-b border-zinc-200 px-5 py-4"
                data-testid={`cart-line-${it.product_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-sm font-semibold">{it.product_name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      Rate{" "}
                      <input
                        type="number"
                        value={it.rate}
                        onChange={(e) => updateLine(it.line_id, "rate", e.target.value)}
                        className="w-16 border border-zinc-200 px-1 py-0.5 font-mono-num"
                        data-testid={`line-rate-${it.product_id}`}
                      />
                      /{it.unit === "piece" ? "pc" : "kg"}
                    </div>
                  </div>
                  <button
                    onClick={() => removeLine(it.line_id)}
                    className="text-zinc-400 hover:text-sky-600"
                    data-testid={`line-remove-${it.product_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      {it.unit === "piece" ? "Pcs" : "Kg"}
                    </div>
                    <input
                      type="number"
                      step={it.unit === "piece" ? "1" : "0.001"}
                      value={it.kg}
                      onChange={(e) => updateLine(it.line_id, "kg", e.target.value)}
                      data-testid={`line-kg-${it.product_id}`}
                      className="w-full border border-zinc-300 px-3 py-2 font-mono-num text-base focus:outline-none focus:ring-2 focus:ring-sky-600"
                    />
                  </div>
                  <div className="text-zinc-400 text-xl pt-5">=</div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      ₹
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={it.amount}
                      onChange={(e) => updateLine(it.line_id, "amount", e.target.value)}
                      data-testid={`line-amount-${it.product_id}`}
                      className="w-full border border-zinc-300 px-3 py-2 font-mono-num text-base focus:outline-none focus:ring-2 focus:ring-sky-600"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Totals + checkout */}
        <div className="border-t border-zinc-200 px-5 py-4 space-y-3 bg-zinc-50">
          {totals.totalKg > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-zinc-600">Total Kg</span>
              <span className="font-mono-num font-semibold" data-testid="pos-total-kg">
                {fmtNum(totals.totalKg, 3)} kg
              </span>
            </div>
          )}
          {totals.totalPcs > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-zinc-600">Total Pieces</span>
              <span className="font-mono-num font-semibold" data-testid="pos-total-pcs">
                {Math.round(totals.totalPcs)} pcs
              </span>
            </div>
          )}
          <div className="flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-wider text-zinc-500">
              Grand Total
            </span>
            <span
              className="font-display font-bold text-3xl font-mono-num"
              data-testid="pos-total-amount"
            >
              {fmtINR(totals.totalAmount)}
            </span>
          </div>
          <input
            type="number"
            placeholder={`Paid (default ${payMode === "credit" ? "₹0" : fmtINR(totals.totalAmount)})`}
            value={paid}
            onChange={(e) => setPaid(e.target.value)}
            data-testid="pos-paid-input"
            className="w-full border border-zinc-300 px-3 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          />
          {/* Payment mode */}
          <div className="grid grid-cols-3 gap-1" data-testid="pos-paymode">
            {["cash", "upi", "credit"].map((m) => (
              <button
                key={m}
                onClick={() => setPayMode(m)}
                data-testid={`pos-paymode-${m}`}
                className={`py-2 text-xs uppercase tracking-wider border transition-colors ${
                  payMode === m
                    ? m === "credit"
                      ? "bg-rose-600 border-rose-600 text-white"
                      : "bg-zinc-900 border-zinc-900 text-white"
                    : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          {payMode === "credit" && (
            <div className="flex gap-1" data-testid="pos-credit-customer">
              <select
                value={creditCustId}
                onChange={(e) => setCreditCustId(e.target.value)}
                data-testid="pos-credit-customer-select"
                className="flex-1 border border-rose-300 bg-rose-50 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
              >
                <option value="">Select customer…</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.phone ? ` · ${c.phone}` : ""}
                  </option>
                ))}
              </select>
              <button
                onClick={quickAddCustomer}
                data-testid="pos-credit-add-customer"
                className="border border-zinc-300 px-3 text-xs uppercase tracking-wider hover:bg-zinc-100 shrink-0"
              >
                + New
              </button>
            </div>
          )}
          <button
            onClick={checkout}
            disabled={cart.length === 0}
            data-testid="pos-checkout-btn"
            className="w-full bg-sky-600 text-white py-4 font-display font-bold text-lg uppercase tracking-wider hover:bg-sky-700 active:scale-[0.98] transition-all disabled:bg-zinc-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Receipt className="h-5 w-5" />
            Charge {fmtINR(totals.totalAmount)}
          </button>
        </div>
      </aside>
      <BillShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        sale={lastSale}
      />
    </div>
  );
}
